// ─── CONVERSATION LINKS — cross-conversation shared memory ──────────────────
//
// Users explicitly link DMs and RP stories together ("these belong to one
// continuity"). When you write in a linked conversation, the prompt builder
// pulls COMPRESSED, RELEVANCE-GATED memory from every linked conversation —
// never raw transcripts — so a linked scene with 10k messages of history
// only surfaces the handful of facts that matter to the current beat.
//
// Why this is cheap (well under the $4/user/day ceiling):
//   • Tier 1 (roster) + Tier 3 (rolling summaries) are plain DB reads of
//     summary rows that EXISTING crons already generate. Zero new AI calls.
//   • Tier 2 (relevance-gated facts) is a client-side keyword-overlap scorer
//     over world_facts / story_events / dm_conversation_facts that are also
//     already extracted in the background. Zero new AI calls.
//   • The whole assembled section is hard-capped (~2.5k tokens) and sits in
//     a stable region of the system prompt, so prompt caching makes the
//     per-turn marginal cost a rounding error after the first turn.
//
// The ONLY thing a link changes at write-time is which rows we SELECT — it
// widens existing queries, it does not add model calls.

import { supabase, safeGetSession } from './supabaseClient.js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://dxdwnpnavlohkwtxfwgq.supabase.co';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const EMBED_FN_URL = `${SUPABASE_URL}/functions/v1/embed-memory`;

// ── Token budgeting (rough: ~4 chars/token) ───────────────────────────────
const CHARS_PER_TOKEN = 4;
const SECTION_TOKEN_CAP = 2500;            // hard cap for the whole linked block
const SECTION_CHAR_CAP = SECTION_TOKEN_CAP * CHARS_PER_TOKEN;
const ROLLING_SUMMARY_CHAR_CAP = 1400;     // per linked convo's rolling summary slice
const MAX_RELEVANCE_FACTS = 14;            // top-N relevance-matched facts/events
const MAX_LINKED_FOR_DEEP = 8;             // don't deep-scan more than this many links

// ── Canonical endpoint ordering ───────────────────────────────────────────
// A link is symmetric. We store one row with the lexically-smaller
// "type:id" endpoint in the a_* columns (matches the DB CHECK constraint).
function endpointKey(type, id) { return `${type}:${id}`; }
function canonicalPair(t1, i1, t2, i2) {
  const k1 = endpointKey(t1, i1);
  const k2 = endpointKey(t2, i2);
  return k1 <= k2
    ? { a_type: t1, a_id: i1, b_type: t2, b_id: i2 }
    : { a_type: t2, a_id: i2, b_type: t1, b_id: i1 };
}

// ──────────────────────────────────────────────────────────────────────────
// LINK / UNLINK
// ──────────────────────────────────────────────────────────────────────────

/**
 * Link two conversations (symmetric — order of args doesn't matter).
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export async function linkConversation(t1, i1, t2, i2, userId) {
  if (!userId || !i1 || !i2) return { ok: false, error: 'missing args' };
  if (t1 === t2 && i1 === i2) return { ok: false, error: 'cannot link a conversation to itself' };
  try {
    await safeGetSession();
    const pair = canonicalPair(t1, i1, t2, i2);
    const { error } = await supabase
      .from('conversation_links')
      .insert({ user_id: userId, ...pair });
    // 23505 = unique_violation → already linked, treat as success (idempotent)
    if (error && error.code !== '23505') {
      console.warn('[conversationLinks] link failed:', error.message);
      return { ok: false, error: error.message };
    }
    return { ok: true };
  } catch (err) {
    console.warn('[conversationLinks] link threw:', err?.message || err);
    return { ok: false, error: err?.message || 'unknown error' };
  }
}

/**
 * Unlink two conversations (symmetric).
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export async function unlinkConversation(t1, i1, t2, i2, userId) {
  if (!userId || !i1 || !i2) return { ok: false, error: 'missing args' };
  try {
    await safeGetSession();
    const pair = canonicalPair(t1, i1, t2, i2);
    const { error } = await supabase
      .from('conversation_links')
      .delete()
      .eq('user_id', userId)
      .eq('a_type', pair.a_type).eq('a_id', pair.a_id)
      .eq('b_type', pair.b_type).eq('b_id', pair.b_id);
    if (error) {
      console.warn('[conversationLinks] unlink failed:', error.message);
      return { ok: false, error: error.message };
    }
    return { ok: true };
  } catch (err) {
    console.warn('[conversationLinks] unlink threw:', err?.message || err);
    return { ok: false, error: err?.message || 'unknown error' };
  }
}

/**
 * All conversations directly linked to (type,id). Direct links only — not
 * transitive — so the user always knows exactly what's connected.
 * @returns {Promise<Array<{type:'dm'|'story', id:string}>>}
 */
export async function getLinkedConversationRefs(type, id, userId) {
  if (!userId || !id) return [];
  try {
    await safeGetSession();
    const { data, error } = await supabase
      .from('conversation_links')
      .select('a_type,a_id,b_type,b_id')
      .eq('user_id', userId)
      .or(`and(a_type.eq.${type},a_id.eq.${id}),and(b_type.eq.${type},b_id.eq.${id})`);
    if (error) {
      console.warn('[conversationLinks] getLinkedRefs failed:', error.message);
      return [];
    }
    const refs = [];
    for (const row of (data || [])) {
      // Return the OTHER endpoint
      if (row.a_type === type && row.a_id === id) {
        refs.push({ type: row.b_type, id: row.b_id });
      } else {
        refs.push({ type: row.a_type, id: row.a_id });
      }
    }
    return refs;
  } catch (err) {
    console.warn('[conversationLinks] getLinkedRefs threw:', err?.message || err);
    return [];
  }
}

/**
 * Hydrate link refs with titles + character ids for the link-menu UI.
 * @returns {Promise<Array<{type, id, title, characterId}>>}
 */
export async function getLinkedConversations(type, id, userId) {
  const refs = await getLinkedConversationRefs(type, id, userId);
  if (refs.length === 0) return [];
  const dmIds = refs.filter(r => r.type === 'dm').map(r => r.id);
  const storyIds = refs.filter(r => r.type === 'story').map(r => r.id);
  const out = [];
  try {
    if (dmIds.length) {
      const { data } = await supabase
        .from('dm_conversations')
        .select('id,title,character_id')
        .in('id', dmIds);
      for (const d of (data || [])) {
        out.push({ type: 'dm', id: d.id, title: d.title || 'Untitled DM', characterId: d.character_id });
      }
    }
    if (storyIds.length) {
      const { data } = await supabase
        .from('stories')
        .select('id,name')
        .in('id', storyIds);
      for (const s of (data || [])) {
        out.push({ type: 'story', id: s.id, title: s.name || 'Untitled RP', characterId: null });
      }
    }
  } catch (err) {
    console.warn('[conversationLinks] hydrate failed:', err?.message || err);
  }
  return out;
}

/**
 * Every DM + RP story the user has, for populating the link-menu picker.
 * Excludes the current conversation. Sorted most-recent-first.
 * @returns {Promise<Array<{type, id, title, characterId, updatedAt}>>}
 */
export async function listAllLinkableConversations(userId, excludeType, excludeId) {
  if (!userId) return [];
  try {
    await safeGetSession();
    const [dmRes, storyRes] = await Promise.all([
      supabase.from('dm_conversations')
        .select('id,title,character_id,last_message_at,updated_at')
        .eq('user_id', userId),
      supabase.from('stories')
        .select('id,name,last_active_at,updated_at')
        .eq('user_id', userId)
        .is('deleted_at', null),
    ]);
    const items = [];
    for (const d of (dmRes.data || [])) {
      if (excludeType === 'dm' && d.id === excludeId) continue;
      items.push({
        type: 'dm', id: d.id,
        title: d.title || 'Untitled DM',
        characterId: d.character_id,
        updatedAt: d.last_message_at || d.updated_at || null,
      });
    }
    for (const s of (storyRes.data || [])) {
      if (excludeType === 'story' && s.id === excludeId) continue;
      items.push({
        type: 'story', id: s.id,
        title: s.name || 'Untitled RP',
        characterId: null,
        updatedAt: s.last_active_at || s.updated_at || null,
      });
    }
    items.sort((a, b) => {
      const ta = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const tb = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return tb - ta;
    });
    return items;
  } catch (err) {
    console.warn('[conversationLinks] listAll failed:', err?.message || err);
    return [];
  }
}

// ──────────────────────────────────────────────────────────────────────────
// RELEVANCE-GATED CROSS-CONVERSATION MEMORY
// ──────────────────────────────────────────────────────────────────────────

// Words too common to be useful as relevance signal.
const STOPWORDS = new Set(`the a an and or but if then else of to in on at by for with
  without from as is are was were be been being have has had do does did will would
  shall should may might must can could i you he she it we they me him her us them
  my your his its our their this that these those there here what which who whom
  not no yes so just very really about into over under up down out off again once
  said says say like get got go went going come came back one two`.split(/\s+/));

/** Pull the distinctive tokens out of the current scene text for matching. */
function extractKeywords(text, max = 40) {
  if (!text) return [];
  const counts = new Map();
  // Proper nouns (Capitalized words) get a weight bump — names/places matter most.
  const properNouns = new Set();
  for (const m of String(text).matchAll(/\b[A-Z][a-z]{2,}\b/g)) {
    properNouns.add(m[0].toLowerCase());
  }
  for (const raw of String(text).toLowerCase().split(/[^a-z0-9']+/)) {
    const w = raw.trim();
    if (w.length < 3 || STOPWORDS.has(w)) continue;
    counts.set(w, (counts.get(w) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([word, freq]) => ({ word, weight: freq + (properNouns.has(word) ? 3 : 0) }))
    .sort((a, b) => b.weight - a.weight)
    .slice(0, max);
}

/** Overlap score between a memory row's text and the scene keywords. */
function relevanceScore(memText, keywords) {
  if (!memText) return 0;
  const lower = memText.toLowerCase();
  let score = 0;
  for (const { word, weight } of keywords) {
    if (lower.includes(word)) score += weight;
  }
  return score;
}

function approxTokens(str) { return Math.ceil((str || '').length / CHARS_PER_TOKEN); }

// ── SEMANTIC RETRIEVAL ────────────────────────────────────────────────────
// The high-ROI path: instead of lexical keyword overlap, embed the current
// scene beat and let pgvector cosine-rank the linked conversations' facts by
// MEANING. "she touched the scar on his shoulder" can pull "Cruz was wounded
// at the gala" with zero shared words.
//
// Cost: embedding ~150 tokens of scene text per turn via Gemini
// text-embedding-004 — fractions of a cent, and it runs in parallel with the
// other prompt-building reads so it adds ~0ms wall-clock. The cosine ranking
// itself happens in Postgres (match_linked_memory RPC) — zero AI cost.
//
// Degrades gracefully: if the embed call fails, or the linked rows aren't
// embedded yet (mid-backfill), the caller falls back to the keyword scorer.

// Character budget for the per-turn query string. embedSceneText truncates to
// the same number, so buildSceneQueryText below is what decides WHICH end of
// the scene survives that truncation.
export const SCENE_QUERY_BUDGET = 1600;

/**
 * Build the text that becomes the per-turn semantic query vector.
 *
 * ORDER IS THE WHOLE POINT. Every caller used to do
 *   history.slice(-12).map(text).join('\n')
 * and let embedSceneText's `.slice(0, 1600)` do the truncating. That join is
 * OLDEST-FIRST, so slicing the FIRST 1600 characters kept the oldest messages
 * and threw away the newest. Measured against a live story: the last 12
 * messages were 6,809 characters, so the query vector was built from messages
 * 1-3 and the other nine — including the line the player had just written —
 * never reached retrieval at all. Every semantic lookup keyed off that vector
 * (match_character_voice for the responder, the player's character and up to
 * four other NPCs, plus match_linked_memory), so all of them were answering a
 * question about what the scene was doing ten turns ago.
 *
 * Now the budget is filled from the NEWEST message backwards and the result is
 * restored to chronological order for the embedder. The most recent message is
 * always in the query; older ones fill whatever budget is left.
 */
export function buildSceneQueryText(messages, opts = {}) {
  const { budget = SCENE_QUERY_BUDGET, maxMessages = 12 } = opts;
  const list = Array.isArray(messages) ? messages : [];
  const texts = list
    .slice(-maxMessages)
    .map(m => (typeof m === 'string' ? m : String(m?.content || m?.text || '')).trim())
    .filter(Boolean);
  if (texts.length === 0) return '';

  const picked = [];
  let used = 0;
  for (let i = texts.length - 1; i >= 0; i--) {
    const remaining = budget - used;
    if (remaining <= 0) break;
    const t = texts[i];
    if (t.length <= remaining) {
      picked.push(t);
      used += t.length + 1; // +1 for the join newline
    } else {
      // Doesn't fit whole. Keep its TAIL — for the newest message that's the
      // current beat, which is exactly what retrieval should be asking about.
      picked.push(t.slice(-remaining));
      break;
    }
  }
  return picked.reverse().join('\n');
}

/**
 * Embed one short string via the embed-memory edge function. Returns the
 * 768-float vector, or null on any failure (callers fall back gracefully).
 *
 * EXPORTED + reused: this is the single per-turn embedding call. The caller
 * (DMView / SixGuysRP) embeds the current scene beat ONCE and feeds the
 * resulting vector into BOTH buildLinkedMemorySection (cross-conversation
 * memory) AND getCharacterDiscordContext (semantic voice retrieval). One
 * embed call, two features — that's the cost multiplier.
 */
export async function embedSceneText(text) {
  const trimmed = (text || '').replace(/\s+/g, ' ').trim().slice(0, SCENE_QUERY_BUDGET);
  if (!trimmed) return null;
  try {
    const { data: { session } } = await safeGetSession();
    const token = session?.access_token;
    if (!token) return null;
    const res = await fetch(EMBED_FN_URL, {
      method: 'POST',
      // 90s guard (audit P2-14): a hung background call used to pin its promise across iOS suspend/resume forever.
      signal: AbortSignal.timeout(90000),
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ mode: 'query', text: trimmed }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return Array.isArray(data?.embedding) ? data.embedding : null;
  } catch (err) {
    console.warn('[conversationLinks] embedSceneText failed (will fall back to keywords):', err?.message || err);
    return null;
  }
}

/**
 * Semantic relevance match over the linked conversations' embedded memory.
 * Takes a pre-computed scene embedding (so the per-turn embed call is shared
 * with voice retrieval). Returns [{ kind, text }] ranked by match_linked_memory,
 * or null if the semantic path isn't usable so the caller can fall back to
 * the keyword scorer.
 *
 * Wire format: p_query_embedding is halfvec(768) (was vector pre-2026-05-15
 * halfvec migration). We MUST pre-stringify the embedding as '[v1,v2,...]'
 * before sending — PostgREST's implicit JSON-array → halfvec coercion path
 * doesn't use the HNSW index and times out on any non-trivial table (verified
 * against rp_history's 35k-row corpus; same shape will bite world_facts and
 * story_events at scale). The text literal goes through pgvector's normal
 * input parser, the planner picks the HNSW index, query is fast.
 */
async function semanticRelevantMemory({ userId, linkedIds, sceneEmbedding, sceneCharacterIds }) {
  const queryEmbedding = sceneEmbedding;
  if (!queryEmbedding) return null;
  try {
    // v2 (2026-10-06) returns happened_at so every line can be dated in the prompt.
    const { data, error } = await supabase.rpc('match_linked_memory_v2', {
      p_user_id: userId,
      // halfvec(768) param — must be sent as a pgvector text literal, not
      // a raw number[]. See JSDoc above.
      p_query_embedding: '[' + queryEmbedding.join(',') + ']',
      p_linked_ids: linkedIds,
      p_scene_characters: Array.isArray(sceneCharacterIds) ? sceneCharacterIds : [],
      p_limit: MAX_RELEVANCE_FACTS,
    });
    if (error) {
      console.warn('[conversationLinks] match_linked_memory RPC failed:', error.message);
      return null;
    }
    if (!data || data.length === 0) return null; // nothing embedded yet → fall back
    return data.map(r => ({ kind: r.kind, text: r.text_content, when: r.happened_at ? String(r.happened_at).slice(0, 10) : null }));
  } catch (err) {
    console.warn('[conversationLinks] semantic match threw:', err?.message || err);
    return null;
  }
}

/**
 * Build the "LINKED STORYLINES" system-prompt section for the conversation
 * currently being written in. Pure DB reads + client-side scoring — no AI.
 *
 * @param {object} args
 * @param {'dm'|'story'} args.currentType
 * @param {string} args.currentId
 * @param {string} args.userId
 * @param {string} [args.recentSceneText]  concatenated recent messages of the
 *        current scene — used for the keyword fallback path.
 * @param {number[]} [args.sceneEmbedding]  pre-computed 768-dim embedding of
 *        the scene beat. Pass this (shared with voice retrieval) to avoid a
 *        second embed call. If omitted, this fn embeds recentSceneText itself.
 * @param {string[]} [args.sceneCharacterIds]  character ids present in the
 *        current scene — facts about them get a relevance boost.
 * @returns {Promise<string>}  the section (incl. leading newlines) or ''.
 */
export async function buildLinkedMemorySection({ currentType, currentId, userId, recentSceneText, sceneEmbedding, sceneCharacterIds }) {
  try {
    if (!userId || !currentId) return '';
    const linked = await getLinkedConversations(currentType, currentId, userId);
    if (linked.length === 0) return '';

    const dmIds = linked.filter(l => l.type === 'dm').map(l => l.id);
    const storyIds = linked.filter(l => l.type === 'story').map(l => l.id);
    const allLinkedIds = linked.map(l => l.id);
    const keywords = extractKeywords(recentSceneText, 40);

    // Use the shared scene embedding if the caller passed one; otherwise
    // embed here (back-compat for callers that don't share the vector).
    const embeddingP = sceneEmbedding
      ? Promise.resolve(sceneEmbedding)
      : embedSceneText(recentSceneText);

    // Kick off the semantic match in parallel with the bulk row fetch below.
    // Resolves to null if the semantic path isn't usable yet (mid-backfill,
    // no key, etc.) — we fall back to the keyword scorer further down.
    const semanticP = embeddingP.then(emb => emb
      ? semanticRelevantMemory({ userId, linkedIds: allLinkedIds, sceneEmbedding: emb, sceneCharacterIds })
      : null);

    // ── Fetch all the pre-computed memory rows for the linked set in parallel.
    // Every one of these tables is populated by existing background jobs;
    // we're only widening the WHERE clause, never calling a model.
    const [
      dmConvRes, storyRes, dmSummRes, storySummRes,
      worldFactsRes, storyEventsRes,
    ] = await Promise.all([
      dmIds.length
        ? supabase.from('dm_conversations').select('id,title,rolling_summary').in('id', dmIds)
        : Promise.resolve({ data: [] }),
      storyIds.length
        ? supabase.from('stories').select('id,name,rolling_summary').in('id', storyIds)
        : Promise.resolve({ data: [] }),
      dmIds.length
        ? supabase.from('dm_conversation_summaries')
            .select('conversation_id,story_arc,unresolved_threads').in('conversation_id', dmIds)
        : Promise.resolve({ data: [] }),
      storyIds.length
        ? supabase.from('story_summaries')
            .select('story_id,story_arc,unresolved_threads').in('story_id', storyIds)
        : Promise.resolve({ data: [] }),
      // world_facts.source_id is the dm/story UUID it was extracted from
      allLinkedIds.length
        ? supabase.from('world_facts')
            .select('fact_text,subject,importance,source_id,active,created_at')
            .eq('user_id', userId)
            .in('source_id', allLinkedIds)
        : Promise.resolve({ data: [] }),
      // story_events.context_id is the dm/story UUID
      allLinkedIds.length
        ? supabase.from('story_events')
            .select('event_text,event_type,importance,context_id,created_at')
            .eq('user_id', userId)
            .in('context_id', allLinkedIds)
        : Promise.resolve({ data: [] }),
    ]);

    const arcByDm = {};
    for (const r of (dmSummRes.data || [])) arcByDm[r.conversation_id] = r;
    const arcByStory = {};
    for (const r of (storySummRes.data || [])) arcByStory[r.story_id] = r;
    const rollingByDm = {};
    for (const r of (dmConvRes.data || [])) rollingByDm[r.id] = r.rolling_summary;
    const rollingByStory = {};
    for (const r of (storyRes.data || [])) rollingByStory[r.id] = r.rolling_summary;

    // ── TIER 1: link roster — one line per linked convo, always included.
    const rosterLines = [];
    for (const l of linked) {
      const arc = (l.type === 'dm' ? arcByDm[l.id]?.story_arc : arcByStory[l.id]?.story_arc) || '';
      const oneLiner = arc
        ? arc.replace(/\s+/g, ' ').trim().slice(0, 160)
        : '(no summary yet)';
      rosterLines.push(`  • ${l.type === 'dm' ? 'DM' : 'RP'} "${l.title}" — ${oneLiner}`);
    }

    // ── TIER 2: relevance-gated facts + events.
    // Primary path: semantic match (embeddings + pgvector cosine, scored in
    // Postgres). Fallback: client-side keyword overlap — used when the
    // semantic path returns null (mid-backfill, no embedding key, RPC error).
    let topFacts = [];
    const semanticHits = await semanticP;
    if (semanticHits && semanticHits.length) {
      topFacts = semanticHits.slice(0, MAX_RELEVANCE_FACTS);
    } else {
      const scored = [];
      for (const f of (worldFactsRes.data || [])) {
        if (f.active === false) continue;
        const text = f.subject ? `${f.subject}: ${f.fact_text}` : f.fact_text;
        const rel = relevanceScore(text, keywords);
        // importance (1-5ish) gives a weak baseline so high-importance canon
        // still surfaces even with no keyword hit; relevance dominates.
        const score = rel * 3 + (f.importance || 0);
        if (score > 0) scored.push({ score, kind: 'fact', text, when: f.created_at ? String(f.created_at).slice(0, 10) : null });
      }
      for (const e of (storyEventsRes.data || [])) {
        const text = e.event_text;
        const rel = relevanceScore(text, keywords);
        const score = rel * 3 + (e.importance || 0);
        if (score > 0) scored.push({ score, kind: 'event', text, when: e.created_at ? String(e.created_at).slice(0, 10) : null });
      }
      scored.sort((a, b) => b.score - a.score);
      topFacts = scored.slice(0, MAX_RELEVANCE_FACTS);
    }

    // ── TIER 3: rolling summaries — the linked convos' compressed "story so
    // far". Capped per-convo and only for the first few links so the section
    // can't balloon. The roster + relevance facts already cover the rest.
    const rollingBlocks = [];
    for (const l of linked.slice(0, MAX_LINKED_FOR_DEEP)) {
      const rolling = l.type === 'dm' ? rollingByDm[l.id] : rollingByStory[l.id];
      if (!rolling) continue;
      const sliced = rolling.replace(/\s+/g, ' ').trim().slice(0, ROLLING_SUMMARY_CHAR_CAP);
      const unresolved = (l.type === 'dm' ? arcByDm[l.id]?.unresolved_threads : arcByStory[l.id]?.unresolved_threads) || '';
      let block = `▸ ${l.type === 'dm' ? 'DM' : 'RP'} "${l.title}"\n${sliced}`;
      if (unresolved) block += `\nOpen threads: ${unresolved.replace(/\s+/g, ' ').trim().slice(0, 400)}`;
      rollingBlocks.push(block);
    }

    // ── Cross-tier dedup: don't spend tokens on a Tier-2 fact whose wording
    // is already sitting in a Tier-3 rolling summary. Conservative — only
    // drops near-verbatim repeats (a normalized 50-char head-substring match).
    const rollingHaystack = rollingBlocks.join(' ').toLowerCase().replace(/\s+/g, ' ');
    const dedupedFacts = topFacts.filter(f => {
      const norm = (f.text || '').toLowerCase().replace(/\s+/g, ' ').trim();
      if (norm.length < 20) return true;
      return !rollingHaystack.includes(norm.slice(0, 50));
    });

    // ── Assemble, newest/most-relevant first, hard-capped at SECTION_CHAR_CAP.
    let body = '';
    body += 'LINK ROSTER (continuity these scenes share):\n' + rosterLines.join('\n') + '\n';

    if (dedupedFacts.length) {
      // 2026-10-06: every line is dated. An undated June night read as tonight.
      body += '\nRELEVANT TO THIS MOMENT (canon pulled from the linked scenes — treat as true, and as the PAST on the date shown; nothing here is happening now):\n';
      for (const f of dedupedFacts) {
        const line = `  • ${f.when ? `[${f.when}] ` : ''}${f.text.replace(/\s+/g, ' ').trim()}\n`;
        if (approxTokens(body + line) > SECTION_TOKEN_CAP) break;
        body += line;
      }
    }

    if (rollingBlocks.length) {
      const header = '\nWHERE THE LINKED SCENES STAND:\n';
      if (approxTokens(body + header) < SECTION_TOKEN_CAP) {
        body += header;
        for (const block of rollingBlocks) {
          if (approxTokens(body + block) > SECTION_TOKEN_CAP) break;
          body += block + '\n\n';
        }
      }
    }

    if (body.length > SECTION_CHAR_CAP) body = body.slice(0, SECTION_CHAR_CAP) + '…';

    return `\n\n═══ 🔗 LINKED STORYLINES ═══
The player has explicitly linked this conversation to the scenes below — they
are ONE shared continuity. What happened there is real here. Reference it
naturally when relevant; never contradict it. Do not invent additional
events for the linked scenes beyond what's stated.

${body.trim()}
═══════════════════════════════════════════\n`;
  } catch (err) {
    // Cross-conversation memory is best-effort — never break a reply over it.
    console.warn('[conversationLinks] buildLinkedMemorySection failed (non-fatal):', err?.message || err);
    return '';
  }
}
